/**
 * Owner-only, read-only presentation of the imported MinbarLive pilot result.
 *
 * There is no run/retry/approve/promote/send/import/edit action here. The panel only
 * fetches while it is mounted for the exact owner project, fences every response by
 * project/report identity, and refuses malformed or inconsistent data through the
 * closed parser before any value reaches the DOM.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, ApiError } from "../api/client";
import {
  MINBAR_PILOT_PROJECT_ID,
  PilotAnalysisState,
  PilotAvailable,
  PilotCallState,
  PilotEvidenceState,
  PilotResult,
  PilotRow,
  parsePilotResult,
} from "./minbarPilotResult";

type Lang = "hr" | "en";
type Filter =
  | "all"
  | "source_backed"
  | "no_usable_page"
  | "refused"
  | "other";

export function canShowMinbarPilotPanel(
  user: { is_org_owner?: boolean } | null,
  project: { id?: string } | null,
): boolean {
  return Boolean(user?.is_org_owner) && project?.id === MINBAR_PILOT_PROJECT_ID;
}

export interface PilotLoader {
  getMinbarPilotResult(
    projectId: string,
    options?: { signal?: AbortSignal },
  ): Promise<unknown>;
}

type PanelState =
  | { kind: "loading" }
  | { kind: "ready"; result: PilotResult }
  | { kind: "invalid" }
  | { kind: "error"; error: unknown }
  | { kind: "needs_project" };

const tr = (lang: Lang, hr: string, en: string): string => (lang === "hr" ? hr : en);

function formatInteger(value: number | null, lang: Lang = "hr"): string {
  return value === null ? tr(lang, "nepoznato", "unknown") : value.toLocaleString("en-US");
}

function formatUsdMicros(micros: number): string {
  return `USD ${(micros / 1_000_000).toFixed(6)}`;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function safeError(lang: Lang, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 404) {
      return tr(lang, "Nema pristupa ili rezultat nije dostupan.", "No access or result unavailable.");
    }
    if (error.status === 403) {
      return tr(lang, "Rezultat pilota dostupan je samo vlasniku organizacije.", "The pilot result is owner-only.");
    }
    if (error.status === 503) {
      return tr(
        lang,
        "Rezultat pilota nije dostupan u provjerenom obliku.",
        "The pilot result is not available in a verified form.",
      );
    }
  }
  return tr(lang, "Rezultat pilota trenutačno nije dostupan.", "The pilot result is currently unavailable.");
}

const CALL_LABEL: Record<PilotCallState, { hr: string; en: string }> = {
  completed: { hr: "dovršeno", en: "completed" },
  rejected: { hr: "odbijeno", en: "refused" },
  unknown: { hr: "nepoznato", en: "unknown" },
  failed: { hr: "neuspješno", en: "failed" },
  not_called: { hr: "nije pozvano", en: "not called" },
};

const ANALYSIS_LABEL: Record<PilotAnalysisState, { hr: string; en: string }> = {
  accepted: { hr: "prihvaćeno", en: "accepted" },
  refused: { hr: "odbijeno", en: "refused" },
  unknown: { hr: "nepoznato", en: "unknown" },
  failed: { hr: "neuspješno", en: "failed" },
  not_called: { hr: "nije pozvano", en: "not called" },
};

const EVIDENCE_LABEL: Record<PilotEvidenceState, { hr: string; en: string }> = {
  source_backed: { hr: "s izvorom", en: "source-backed" },
  no_usable_page: { hr: "bez upotrebljive stranice", en: "no usable page" },
  not_applicable: { hr: "nije primjenjivo", en: "not applicable" },
};

function callLabel(lang: Lang, state: PilotCallState): string {
  return lang === "hr" ? CALL_LABEL[state].hr : CALL_LABEL[state].en;
}

function analysisLabel(lang: Lang, state: PilotAnalysisState): string {
  return lang === "hr" ? ANALYSIS_LABEL[state].hr : ANALYSIS_LABEL[state].en;
}

function evidenceLabel(lang: Lang, state: PilotEvidenceState): string {
  return lang === "hr" ? EVIDENCE_LABEL[state].hr : EVIDENCE_LABEL[state].en;
}

function filterMatches(row: PilotRow, filter: Filter): boolean {
  switch (filter) {
    case "source_backed":
      return row.analysis.state === "accepted" && row.evidence.state === "source_backed";
    case "no_usable_page":
      return row.analysis.state === "accepted" && row.evidence.state === "no_usable_page";
    case "refused":
      return row.call.state === "rejected";
    case "other":
      return !["completed", "rejected"].includes(row.call.state);
    default:
      return true;
  }
}

function searchMatches(row: PilotRow, query: string): boolean {
  if (!query) return true;
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return true;
  return [row.organization_name, row.row_id, row.organization_id]
    .some((value) => value.toLocaleLowerCase().includes(needle));
}

export function MinbarPilotPanel({
  projectId,
  lang = "hr",
  loader = api,
}: {
  projectId: string;
  lang?: Lang;
  loader?: PilotLoader;
}) {
  const [state, setState] = useState<PanelState>({ kind: "loading" });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [detailId, setDetailId] = useState<string | null>(null);
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  const detailRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setQuery("");
    setFilter("all");
    setDetailId(null);
    if (projectId !== MINBAR_PILOT_PROJECT_ID) {
      setState({ kind: "needs_project" });
      return undefined;
    }
    let active = true;
    const controller = new AbortController();
    setState({ kind: "loading" });
    void (async () => {
      try {
        const raw = await loader.getMinbarPilotResult(projectId, {
          signal: controller.signal,
        });
        if (!active) return;
        const parsed = parsePilotResult(raw, { projectId });
        if (!active) return;
        setState(parsed === null ? { kind: "invalid" } : { kind: "ready", result: parsed });
      } catch (error) {
        if (!active || controller.signal.aborted || isAbort(error)) return;
        setState({ kind: "error", error });
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [loader, projectId]);

  const available: PilotAvailable | null = useMemo(() => {
    if (state.kind !== "ready" || state.result.state !== "available") return null;
    return state.result.report.project_id === projectId ? state.result : null;
  }, [projectId, state]);

  const fingerprint = available === null
    ? ""
    : [
      projectId,
      available.report.report_id,
      available.timing.view_prepared_at,
      available.report.origin.rows_sha256,
      available.report.origin.result_sha256,
      available.rows.map((row) => row.evidence.pages
        .map((page) => `${page.source_id}:${page.content_hash}`)
        .join(",")).join("|"),
    ].join("|");

  useEffect(() => {
    setDetailId(null);
  }, [fingerprint]);

  const closeDetail = useCallback(() => {
    const current = detailId;
    setDetailId(null);
    if (current) {
      window.requestAnimationFrame(() => triggers.current.get(current)?.focus());
    }
  }, [detailId]);

  useEffect(() => {
    if (!detailId) return undefined;
    detailRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDetail();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeDetail, detailId]);

  const visibleRows = useMemo(() => {
    if (available === null) return [];
    return available.rows.filter(
      (row) => filterMatches(row, filter) && searchMatches(row, query),
    );
  }, [available, filter, query]);

  if (state.kind === "needs_project") {
    return (
      <p className="text-sm text-muted-foreground" data-testid="minbar-pilot-wrong-project">
        {tr(
          lang,
          "Rezultati pilota prikazuju se samo za projekt MinbarLive.",
          "Pilot results are shown only for the MinbarLive project.",
        )}
      </p>
    );
  }

  return (
    <section className="space-y-5" data-testid="minbar-pilot-panel">
      <header className="space-y-2">
        <h2 className="text-lg font-semibold">
          {tr(lang, "Rezultati stvarnog ograničenog pilota", "Real bounded pilot results")}
        </h2>
        <p className="text-sm text-muted-foreground" data-testid="minbar-pilot-origin-note">
          {tr(
            lang,
            "Uvezeni CLI rezultat. Nije aplikacijski posao i nije pokrenut novi pilot.",
            "Imported CLI result. This is not an application job and no new pilot was run.",
          )}
        </p>
        <p className="text-sm text-muted-foreground" data-testid="minbar-pilot-usefulness-note">
          {tr(
            lang,
            "Izvorni rezultati su djelomični; njihova poslovna korisnost je ograničena i nisu ponovno analizirani.",
            "The original results are partial; their business usefulness is limited and they were not re-analysed.",
          )}
        </p>
        <p className="text-sm" data-testid="minbar-pilot-acceptance-note">
          {tr(
            lang,
            "Ljudsko prihvaćanje: na čekanju. „Prošlo provjere” znači samo provjeru sheme i izvora, ne ljudsko odobrenje ni poslovni uspjeh.",
            "Human acceptance: pending. “Passed checks” means schema/source checks only, not human approval or business success.",
          )}
        </p>
      </header>

      {state.kind === "loading" && (
        <p className="text-sm text-muted-foreground" data-testid="minbar-pilot-loading">
          {tr(lang, "Učitavanje rezultata pilota…", "Loading pilot result…")}
        </p>
      )}
      {state.kind === "error" && (
        <p
          className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
          role="alert"
          data-testid="minbar-pilot-error"
        >
          {safeError(lang, state.error)}
        </p>
      )}
      {state.kind === "invalid" && (
        <p
          className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
          role="alert"
          data-testid="minbar-pilot-invalid"
        >
          {tr(
            lang,
            "Rezultat nije moguće sigurno prikazati: identitet, zbrojevi ili izvori nisu sukladni.",
            "The result cannot be shown safely: identity, totals or citations do not match.",
          )}
        </p>
      )}
      {state.kind === "ready" && state.result.state === "unavailable" && (
        <p
          className="rounded border border-border p-4 text-sm text-muted-foreground"
          data-testid="minbar-pilot-unavailable"
        >
          {tr(
            lang,
            "Rezultat pilota još nije konfiguriran na poslužitelju.",
            "The pilot result is not configured on the server yet.",
          )}
        </p>
      )}

      {available !== null && (
        <>
          <PilotSummary lang={lang} result={available} />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block flex-1">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                {tr(lang, "Pretraži organizacije", "Search organizations")}
              </span>
              <input
                type="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setDetailId(null);
                }}
                className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
                placeholder={tr(lang, "Naziv ili identifikator", "Name or identifier")}
                data-testid="minbar-pilot-search"
              />
            </label>
            <label className="block sm:w-64">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                {tr(lang, "Prikaži", "Show")}
              </span>
              <select
                value={filter}
                onChange={(event) => {
                  setFilter(event.target.value as Filter);
                  setDetailId(null);
                }}
                className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
                data-testid="minbar-pilot-filter"
              >
                <option value="all">{tr(lang, "Sve (10)", "All (10)")}</option>
                <option value="source_backed">
                  {tr(lang, "Prošlo · s izvorom", "Passed · source-backed")}
                </option>
                <option value="no_usable_page">
                  {tr(lang, "Prošlo · bez upotrebljive stranice", "Passed · no usable page")}
                </option>
                <option value="refused">{tr(lang, "Odbijeno", "Refused")}</option>
                <option value="other">{tr(lang, "Nepoznato / neuspješno", "Unknown / failed")}</option>
              </select>
            </label>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="minbar-pilot-visible-count">
            {tr(lang, "Prikazano", "Showing")} {visibleRows.length} / {available.rows.length}
          </p>
          <ul className="divide-y divide-border rounded border border-border" aria-label={tr(lang, "Rezultati po organizaciji", "Results by organization")}>
            {visibleRows.map((row) => {
              const expanded = detailId === row.row_id;
              return (
                <li key={row.row_id} data-testid={`minbar-pilot-row-${row.row_number}`}>
                  <button
                    type="button"
                    id={`pilot-row-button-${row.row_number}`}
                    ref={(element) => {
                      if (element) triggers.current.set(row.row_id, element);
                      else triggers.current.delete(row.row_id);
                    }}
                    aria-expanded={expanded}
                    aria-controls={`pilot-detail-${row.row_number}`}
                    onClick={() => setDetailId(expanded ? null : row.row_id)}
                    className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-accent/40 focus-visible:outline focus-visible:outline-2"
                  >
                    <span className="font-mono text-xs text-muted-foreground">
                      {row.row_number}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {row.organization_name}
                    </span>
                    <RowBadge lang={lang} row={row} />
                    <span aria-hidden="true" className="text-xs text-muted-foreground">
                      {expanded ? "▲" : "▼"}
                    </span>
                  </button>
                  {expanded && (
                    <div
                      ref={detailRef}
                      id={`pilot-detail-${row.row_number}`}
                      role="region"
                      aria-labelledby={`pilot-row-button-${row.row_number}`}
                      tabIndex={-1}
                      className="border-t border-border bg-muted/20 px-4 py-4"
                      data-testid={`minbar-pilot-detail-${row.row_number}`}
                    >
                      <PilotRowDetail lang={lang} result={available} row={row} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {visibleRows.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="minbar-pilot-no-match">
              {tr(lang, "Nema organizacija za ovaj filtar.", "No organizations match this filter.")}
            </p>
          )}
          <PilotNotices lang={lang} result={available} />
        </>
      )}
    </section>
  );
}

function RowBadge({ lang, row }: { lang: Lang; row: PilotRow }) {
  const accepted = row.analysis.state === "accepted";
  const style = accepted
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
    : row.analysis.state === "refused"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300"
      : "border-border bg-muted text-muted-foreground";
  return (
    <span
      className={`rounded border px-2 py-0.5 text-xs ${style}`}
      data-testid={`minbar-pilot-badge-${row.row_number}`}
      data-state={row.analysis.state}
      data-evidence={row.evidence.state}
    >
      {accepted ? evidenceLabel(lang, row.evidence.state) : analysisLabel(lang, row.analysis.state)}
    </span>
  );
}

function PilotSummary({ lang, result }: { lang: Lang; result: PilotAvailable }) {
  const { summary } = result;
  const input = summary.usage.prompt_tokens;
  const output = summary.usage.completion_tokens;
  const total = input === null || output === null ? null : input + output;
  const cards = [
    {
      key: "attempted",
      label: tr(lang, "Pokušaji", "Attempts"),
      value: String(summary.attempted),
    },
    {
      key: "accepted",
      label: tr(lang, "Prošlo provjere", "Passed checks"),
      value: String(summary.accepted),
      note: tr(lang, "samo shema i izvori", "schema and sources only"),
    },
    {
      key: "refused",
      label: tr(lang, "Odbijeno", "Refused"),
      value: String(summary.refused),
    },
    {
      key: "no-page",
      label: tr(lang, "Prošlo · bez upotrebljive stranice", "Passed · no usable page"),
      value: String(summary.accepted_no_usable_page),
    },
    {
      key: "source-backed",
      label: tr(lang, "Prošlo · s izvorom", "Passed · source-backed"),
      value: String(summary.accepted_source_backed),
    },
  ];
  return (
    <div className="space-y-3">
      <p className="text-sm" data-testid="minbar-pilot-provenance-note">
        {tr(
          lang,
          `Sažetak je urednički pregled postojećih odgovora; izostavljeno je ${result.provenance.omitted_content_count} sadržaja.`,
          `The summary is a curated presentation of existing responses; ${result.provenance.omitted_content_count} items were omitted.`,
        )}
      </p>
      <p className="text-sm" data-testid="minbar-pilot-summary-scope-note">
        {tr(
          lang,
          "„Prošlo provjere” broji redove koji su prošli provjeru sheme i izvora. To nije poslovni uspjeh, korisnost ni ljudsko prihvaćanje.",
          "“Passed checks” counts rows that passed schema and source checks. It is not business success, usefulness or human acceptance.",
        )}
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map((card) => (
          <div key={card.key} className="rounded border border-border p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold" data-testid={`minbar-pilot-summary-${card.key}`}>
              {card.value}
            </p>
            {"note" in card && card.note && (
              <p className="text-xs text-muted-foreground">{card.note}</p>
            )}
          </div>
        ))}
        <div className="rounded border border-border p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {tr(lang, "Procijenjeni trošak", "Estimated cost")}
          </p>
          <p className="mt-1 text-lg font-semibold" data-testid="minbar-pilot-summary-cost">
            {formatUsdMicros(summary.cost.estimated_micros)}
          </p>
          <p className="text-xs text-muted-foreground">
            {summary.cost.unknown_cost_rows === 0
              ? tr(lang, "nije faktura", "not an invoice")
              : tr(
                lang,
                `nije faktura · ${summary.cost.unknown_cost_rows} redova bez poznate procjene`,
                `not an invoice · ${summary.cost.unknown_cost_rows} rows without a known estimate`,
              )}
          </p>
        </div>
      </div>
      <dl className="grid grid-cols-3 gap-3 rounded border border-border p-3 text-sm sm:max-w-xl">
        <div>
          <dt className="text-xs text-muted-foreground">{tr(lang, "Ulazni tokeni", "Input tokens")}</dt>
          <dd className="font-mono" data-testid="minbar-pilot-summary-input">{formatInteger(input, lang)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{tr(lang, "Izlazni tokeni", "Output tokens")}</dt>
          <dd className="font-mono" data-testid="minbar-pilot-summary-output">{formatInteger(output, lang)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{tr(lang, "Ukupno", "Total")}</dt>
          <dd className="font-mono" data-testid="minbar-pilot-summary-total">{formatInteger(total, lang)}</dd>
        </div>
      </dl>
    </div>
  );
}

function fitUnknownText(lang: Lang, row: PilotRow): string {
  if (row.analysis.state === "refused") {
    return tr(
      lang,
      "Poslovna podobnost nije zadržana jer je vraćeni odgovor odbijen.",
      "Business fit was not retained because the returned answer was refused.",
    );
  }
  if (row.analysis.state === "not_called") {
    return tr(
      lang,
      "Poslovna podobnost nije procijenjena jer poziv nije pokrenut.",
      "Business fit was not assessed because no call was made.",
    );
  }
  if (row.analysis.state !== "accepted") {
    return tr(
      lang,
      "Poslovna podobnost nije procijenjena.",
      "Business fit was not assessed.",
    );
  }
  if (row.evidence.state === "no_usable_page") {
    return tr(
      lang,
      "Nepoznato — nije bila dostupna upotrebljiva stranica za procjenu.",
      "Unknown — no usable page was available for a fit assessment.",
    );
  }
  return tr(
    lang,
    "Nepoznato — dostupni izvori nisu uspostavili poslovnu podobnost.",
    "Unknown — the available evidence did not establish a business fit.",
  );
}

function PilotRowDetail({
  lang,
  result,
  row,
}: {
  lang: Lang;
  result: PilotAvailable;
  row: PilotRow;
}) {
  const pageById = new Map(row.evidence.pages.map((page) => [page.source_id, page]));
  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold">{row.organization_name}</h3>
        <span className="font-mono text-xs text-muted-foreground">{row.organization_id}</span>
        <RowBadge lang={lang} row={row} />
      </div>
      <p className="text-xs text-muted-foreground" data-testid={`minbar-pilot-association-${row.row_number}`}>
        {row.association === "parent_approved_not_independently_verified"
          ? tr(
            lang,
            "Organizacijska povezanost prethodno je zabilježena, ali nije neovisno potvrđena.",
            "The organization association was previously asserted, but is not independently confirmed.",
          )
          : tr(lang, "Organizacijska povezanost nije razriješena.", "The organization association is unresolved.")}
      </p>

      <DetailBlock title={tr(lang, "Neprovjereni prethodni navodi", "Unverified prior claims")}>
        {row.unverified_prior_claims.length === 0 ? (
          <p className="text-muted-foreground">{tr(lang, "Nema zabilježenih navoda.", "No claims recorded.")}</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5">
            {row.unverified_prior_claims.map((claim) => (
              <li key={`${claim.field}:${claim.value}`}>
                <span className="font-mono text-xs text-muted-foreground">{claim.field}</span>{" "}
                <span>{claim.value}</span>{" "}
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  [{tr(lang, "neprovjereno", "unverified")}]
                </span>
              </li>
            ))}
          </ul>
        )}
      </DetailBlock>

      <DetailBlock title={tr(lang, "Prikupljeni izvori", "Collected sources")}>
        <p className="text-xs text-muted-foreground" data-testid={`minbar-pilot-source-counts-${row.row_number}`}>
          {tr(lang, "Dohvaćeno", "Fetched")}: {row.evidence.fetched_page_count} ·{" "}
          {tr(lang, "Modelu ponuđeno", "Supplied to model")}: {row.evidence.model_supplied_source_count} ·{" "}
          {tr(lang, "Izostavljeno", "Omitted")}: {row.evidence.model_omitted_source_count}
        </p>
        <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
          {tr(lang, "Ponuđeni ID-evi", "Supplied IDs")}: {row.evidence.model_supplied_source_ids.join(", ") || "—"}
          {" · "}
          {tr(lang, "Izostavljeni ID-evi", "Omitted IDs")}: {row.evidence.model_omitted_source_ids.join(", ") || "—"}
        </p>
        {row.analysis.state === "refused" && row.evidence.model_supplied_source_count > 0 && (
          <p className="mt-1 text-xs text-muted-foreground" data-testid={`minbar-pilot-refused-sources-${row.row_number}`}>
            {tr(
              lang,
              "Ovi izvori su stvarno dostavljeni modelu; odbijeni odgovor nije zadržan.",
              "These sources were actually supplied to the model; the refused answer is not retained.",
            )}
          </p>
        )}
        {row.evidence.pages.length === 0 ? (
          <p className="mt-2 text-muted-foreground">
            {tr(lang, "Nema prikupljenih javnih stranica.", "No public pages collected.")}
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {row.evidence.pages.map((page) => (
              <li key={page.source_id}>
                <a
                  href={page.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  className="break-all text-primary underline underline-offset-2"
                  data-testid={`minbar-pilot-source-${row.row_number}-${page.source_id}`}
                >
                  {page.title ?? page.url}
                </a>
                <p className="break-all font-mono text-xs text-muted-foreground">
                  {page.source_id} · {page.fetched_at} · {page.content_hash}
                </p>
              </li>
            ))}
          </ul>
        )}
      </DetailBlock>

      <DetailBlock title={tr(lang, "Nove organizacijske opservacije", "New organization observations")}>
        {row.analysis.new_observations.length === 0 ? (
          <p className="text-muted-foreground">{tr(lang, "Nema odabranih opservacija.", "No selected observations.")}</p>
        ) : (
          <ul className="space-y-2">
            {row.analysis.new_observations.map((observation) => {
              const page = pageById.get(observation.source_id);
              return (
                <li key={`${observation.source_id}:${observation.text}`}>
                  <p>{observation.text}</p>
                  <p className="break-all font-mono text-xs text-muted-foreground">
                    {page ? (
                      <a
                        href={page.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        referrerPolicy="no-referrer"
                        className="underline underline-offset-2"
                      >
                        {observation.source_id}
                      </a>
                    ) : observation.source_id}
                    {" · "}{observation.content_hash}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </DetailBlock>

      <DetailBlock title={tr(lang, "Poslovna podobnost", "Business fit")}>
        {row.analysis.business_fit.state === "inference" ? (
          <p>
            <span className="mr-2 rounded border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-xs text-sky-800 dark:text-sky-300">
              {tr(lang, "INFERENCIJA", "INFERENCE")}
            </span>
            <strong>{row.analysis.business_fit.level}</strong>{" "}
            {row.analysis.business_fit.rationale}
          </p>
        ) : (
          <p className="text-muted-foreground" data-testid={`minbar-pilot-fit-${row.row_number}`}>
            {fitUnknownText(lang, row)}
          </p>
        )}
      </DetailBlock>

      <div className="grid gap-4 sm:grid-cols-2">
        <DetailBlock title={tr(lang, "Nedostajuće informacije", "Missing information")}>
          {row.analysis.missing_information.length === 0 ? (
            <p className="text-muted-foreground">{tr(lang, "Nema zabilježenih stavki.", "No items recorded.")}</p>
          ) : (
            <ul className="list-disc space-y-1 pl-5">
              {row.analysis.missing_information.map((item) => <li key={item}>{item}</li>)}
            </ul>
          )}
        </DetailBlock>
        <DetailBlock title={tr(lang, "Predloženi sljedeći pregled", "Suggested next review")}>
          {row.analysis.next_review.length === 0 ? (
            <p className="text-muted-foreground">{tr(lang, "Nema predloženog pregleda.", "No next review proposed.")}</p>
          ) : (
            <ul className="list-disc space-y-1 pl-5">
              {row.analysis.next_review.map((item) => <li key={item}>{item}</li>)}
            </ul>
          )}
        </DetailBlock>
      </div>

      {row.analysis.refusal !== null && (
        <DetailBlock title={tr(lang, "Odbijen ishod", "Refused outcome")}>
          <p data-testid={`minbar-pilot-refusal-${row.row_number}`}>
            {row.analysis.refusal.code === "analysis_invalid_input"
              ? tr(
                lang,
                "Neispravan format vraćenog odgovora",
                "Invalid returned-response format",
              )
              : tr(
                lang,
                "Nepodržana prethodna tvrdnja",
                "Unsupported prior fact",
              )}
            {" · "}
            {tr(lang, "točno polje", "exact field")}: {tr(lang, "nepoznato", "unknown")}
            {" · "}
            {tr(lang, "pojedinost tvrdnje", "claim detail")}:{" "}
            {row.analysis.refusal.claim_detail === null
              ? tr(lang, "nije zabilježena", "not recorded")
              : tr(lang, "nije zadržana", "not retained")}
          </p>
          <p className="mt-1 text-muted-foreground">
            {tr(
              lang,
              "Model je vratio odgovor, ali je odbijen i nije zadržan u ovom prikazu.",
              "The model returned an answer, but it was rejected and is not retained in this view.",
            )}
          </p>
          {row.analysis.refusal.code === "analysis_invalid_input" && (
            <p className="mt-1 text-muted-foreground">
              {tr(
                lang,
                "Odbijen je format vraćenog odgovora; to ne znači da je korisnički unos bio neispravan.",
                "The returned response format was refused; this does not mean the user's input was invalid.",
              )}
            </p>
          )}
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-muted-foreground">
              {tr(lang, "Tehnički detalji", "Technical details")}
            </summary>
            <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
              code: {row.analysis.refusal.code}
              {" · "}root_key_detail: {row.analysis.refusal.root_key_detail}
              {" · "}claim_detail: {row.analysis.refusal.claim_detail ?? "null"}
            </p>
          </details>
        </DetailBlock>
      )}

      <DetailBlock title={tr(lang, "Poziv i trošak", "Call and cost")}>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs sm:grid-cols-3">
          <Pair label={tr(lang, "Stanje", "State")} value={callLabel(lang, row.call.state)} />
          <Pair label={tr(lang, "Zatraženi model", "Requested model")} value={row.call.requested_model} />
          <Pair label={tr(lang, "Vraćeni model", "Returned model")} value={row.call.actual_model ?? "—"} />
          <Pair label={tr(lang, "Pogreška", "Error")} value={row.call.error_code ?? "—"} />
          <Pair label={tr(lang, "Ulaz", "Input")} value={formatInteger(row.call.usage.prompt_tokens, lang)} />
          <Pair label={tr(lang, "Cache hit", "Cache hit")} value={formatInteger(row.call.usage.cache_hit_tokens, lang)} />
          <Pair label={tr(lang, "Cache miss", "Cache miss")} value={formatInteger(row.call.usage.cache_miss_tokens, lang)} />
          <Pair label={tr(lang, "Izlaz", "Output")} value={formatInteger(row.call.usage.completion_tokens, lang)} />
          <Pair label={tr(lang, "Reasoning", "Reasoning")} value={formatInteger(row.call.usage.reasoning_tokens, lang)} />
          <Pair
            label={tr(lang, "Procjena troška", "Estimated cost")}
            value={row.call.estimated_micros === null
              ? tr(lang, "nepoznato", "unknown")
              : formatUsdMicros(row.call.estimated_micros)}
          />
          <Pair
            label={tr(lang, "Response SHA-256", "Response SHA-256")}
            value={row.call.response_sha256 ?? "—"}
            wide
          />
        </dl>
      </DetailBlock>

      <p className="text-xs text-muted-foreground" data-testid={`minbar-pilot-omitted-${row.row_number}`}>
        {tr(lang, "Izostavljeno iz ovog reda", "Omitted from this row")}:{" "}
        {row.presentation.omitted_content_count}. {tr(
          lang,
          "Izvorni puni odgovor nije retroaktivno sanitiziran ni uključen u aplikaciju.",
          "The original full response was not retroactively sanitized and is not included here.",
        )}
      </p>
      <p className="text-xs text-muted-foreground">{result.provenance.notice}</p>
    </div>
  );
}

function Pair({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={wide ? "col-span-2 sm:col-span-3" : undefined}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all">{value}</dd>
    </div>
  );
}

function DetailBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      {children}
    </section>
  );
}

function PilotNotices({ lang, result }: { lang: Lang; result: PilotAvailable }) {
  return (
    <footer className="space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
      <p data-testid="minbar-pilot-code-attribution">
        {tr(lang, "Izvorni kod pilota", "Original pilot code")}: {result.report.origin.code_commit}
        {" · "}
        {tr(lang, "Prikaz", "View")}: {result.report.view.implementation.toUpperCase()}
      </p>
      <p>
        {tr(lang, "Trošak je procjena iz prijavljene potrošnje, nije faktura.", "Cost is an estimate from reported usage, not an invoice.")}
      </p>
      {result.limitations.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5">
          {result.limitations.map((item) => <li key={item}>{item}</li>)}
        </ul>
      )}
    </footer>
  );
}
